// Event day, rental settlement and closeout (rentals and sponsorships plan,
// Phase 6; ADR 0046). Booking stays the record of the rental; Hub owns event
// day (ADR 0018), work orders (ADR 0013) and settlement (ADR 0039, 0040).
//
// Booking's reads (bearer-only on HUB_READ_SECRET, SELECT only):
//   GET /api/internal/hub/v1/rentals/:id/beo                the BEO as data
//   GET /api/internal/hub/v1/rentals/:id/beo.pdf            Booking's BEO PDF, no email addresses
//   GET /api/internal/hub/v1/rentals/:id/settlement-terms   deal terms, tiers, Booking's own settlement, early payouts
//   GET /api/internal/hub/v1/rentals/:id/payouts            early payouts
//   GET /api/internal/hub/v1/rentals/:id/closeout           the closeout and its checklist
//
// And Hub's own rules about them: which Hub event a rental belongs to
// (suggested by date and room, confirmed by a person, never automatic), how
// Booking's rental deal becomes Hub's rental deal, and the closeout
// checklist's Hub items.

import { z } from 'zod'
import type { SettlementDealTerms } from './settlement-deal'

export const bookingBeoPath = (id: string) => `/rentals/${encodeURIComponent(id)}/beo`
export const bookingBeoPdfPath = (id: string) => `/rentals/${encodeURIComponent(id)}/beo.pdf`
export const bookingSettlementTermsPath = (id: string) => `/rentals/${encodeURIComponent(id)}/settlement-terms`
export const bookingPayoutsPath = (id: string) => `/rentals/${encodeURIComponent(id)}/payouts`
export const bookingCloseoutPath = (id: string) => `/rentals/${encodeURIComponent(id)}/closeout`

const Id = z.string().min(1).max(64)
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const Iso = z.string().max(40)
const Text = (max: number) => z.string().max(max)
const Minor = z.number().int()
const Version = z.string().max(20)
const Count = z.number().int().min(0)

// ------------------------------------------------------------------- BEO

export const BookingBeo = z.object({
  version: z.literal(1),
  asOf: Iso,
  rental: z.object({
    sourceId: Id,
    version: Version,
    title: Text(300),
    status: Text(40),
    rentalType: z.enum(['standard', 'ticketed']),
    eventType: Text(100).nullable(),
    expectedAttendance: z.number().int().nullable(),
    clientName: Text(200).nullable(),
    clientPhone: Text(40).nullable(),
    organizationName: Text(200).nullable(),
    ownerName: Text(200).nullable(),
    setupNotes: Text(4000).nullable(),
    planningNotes: Text(4000).nullable(),
    coiStatus: Text(40).nullable(),
    insuranceStatus: Text(40).nullable(),
  }),
  schedule: z.array(
    z.object({
      rentalSpaceId: Id,
      spaceId: Id,
      venueName: Text(200).nullable(),
      roomName: Text(200),
      dayType: z.enum(['event', 'setup', 'cleanup']),
      startAt: Iso.nullable(),
      endAt: Iso.nullable(),
      startLocal: Text(16).nullable(),
      endLocal: Text(16).nullable(),
      eventStartLocal: Text(16).nullable(),
      eventEndLocal: Text(16).nullable(),
      notes: Text(1000).nullable(),
    }),
  ),
  ticketedDates: z.array(z.object({ date: Day.nullable(), type: Text(40), startTime: Text(10).nullable(), endTime: Text(10).nullable(), notes: Text(1000).nullable() })),
  layouts: z.array(z.object({ name: Text(200), locked: z.boolean(), notes: Text(2000).nullable(), elementCount: Count, seats: Count })),
  inventory: z.array(z.object({ itemName: Text(200), category: Text(60).nullable(), quantity: z.number().int(), notes: Text(1000).nullable() })),
  timeline: z.array(z.object({ time: Text(40).nullable(), label: Text(300), notes: Text(1000).nullable() })),
  vendors: z.array(z.object({ name: Text(200), type: Text(100).nullable(), contact: Text(200).nullable(), phone: Text(40).nullable(), arrivalTime: Text(40).nullable(), notes: Text(1000).nullable() })),
  guests: z
    .object({
      expectedAdults: z.number().int().nullable(),
      expectedChildren: z.number().int().nullable(),
      tableCount: z.number().int().nullable(),
      dietaryNotes: Text(2000).nullable(),
      accessibilityNotes: Text(2000).nullable(),
      seatingNotes: Text(2000).nullable(),
    })
    .nullable(),
  checklist: z.array(z.object({ label: Text(300), assignedTo: Text(200).nullable(), dueDate: Day.nullable(), completed: z.boolean() })),
  pdfPath: Text(200),
})
export type BookingBeo = z.infer<typeof BookingBeo>

// The run of show for event day, in time order: Booking's room times and
// the rental's own timeline, each saying where it came from (rule 11).
export type RunOfShowEntry = { at: string | null; sortKey: string; label: string; detail: string | null; source: 'room' | 'timeline' | 'vendor' }

export function bookingRunOfShow(beo: BookingBeo, day: string | null): RunOfShowEntry[] {
  const out: RunOfShowEntry[] = []
  for (const s of beo.schedule) {
    if (day && s.startLocal && s.startLocal.slice(0, 10) !== day && s.endLocal?.slice(0, 10) !== day) continue
    const room = `${s.roomName}${s.venueName ? ` (${s.venueName})` : ''}`
    const kind = s.dayType === 'setup' ? 'Setup' : s.dayType === 'cleanup' ? 'Cleanup' : 'Space'
    if (s.startLocal) out.push({ at: s.startLocal.slice(11), sortKey: s.startLocal, label: `${kind}: ${room} from`, detail: s.notes, source: 'room' })
    if (s.eventStartLocal) out.push({ at: s.eventStartLocal.slice(11), sortKey: s.eventStartLocal, label: `Event starts: ${room}`, detail: null, source: 'room' })
    if (s.eventEndLocal) out.push({ at: s.eventEndLocal.slice(11), sortKey: s.eventEndLocal, label: `Event ends: ${room}`, detail: null, source: 'room' })
    if (s.endLocal) out.push({ at: s.endLocal.slice(11), sortKey: s.endLocal, label: `${kind}: ${room} until`, detail: null, source: 'room' })
  }
  // The rental's timeline and vendor arrivals carry typed times ("4:00 PM",
  // "16:00"); they sort among the room times by their 24-hour reading.
  const prefix = day ?? beo.schedule.find((s) => s.dayType === 'event')?.startLocal?.slice(0, 10) ?? ''
  for (const t of beo.timeline) out.push({ at: t.time, sortKey: `${prefix}T${clock(t.time) ?? '99:99'}`, label: t.label, detail: t.notes, source: 'timeline' })
  for (const v of beo.vendors) if (v.arrivalTime) out.push({ at: v.arrivalTime, sortKey: `${prefix}T${clock(v.arrivalTime) ?? '99:99'}`, label: `Vendor arrives: ${v.name}${v.type ? ` (${v.type})` : ''}`, detail: v.notes, source: 'vendor' })
  return out.sort((a, b) => a.sortKey.localeCompare(b.sortKey))
}

/** "4:00 PM", "4 pm", "16:00" → "16:00"; anything else → null. */
export function clock(typed: string | null): string | null {
  if (!typed) return null
  const m = typed.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*([ap]\.?m\.?)?$/i)
  if (!m) return null
  let h = Number(m[1])
  const min = Number(m[2] ?? '0')
  const ap = m[3]?.toLowerCase().replace(/\./g, '')
  if (min > 59 || h > 23) return null
  if (ap === 'pm' && h < 12) h += 12
  if (ap === 'am' && h === 12) h = 0
  if (ap && Number(m[1]) > 12) return null
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

// ----------------------------------------------------- settlement terms

export const BookingRentalPayout = z.object({
  sourceId: Id,
  amountMinor: Minor,
  paidOn: Day.nullable(),
  method: z.enum(['check', 'cash', 'wire', 'other']),
  authorizedByName: Text(200).nullable(),
  notes: Text(1000).nullable(),
  recordedAt: Iso.nullable(),
})
export type BookingRentalPayout = z.infer<typeof BookingRentalPayout>

export const BookingRentalPayouts = z.object({ version: z.literal(1), asOf: Iso, rentalId: Id, payouts: z.array(BookingRentalPayout), totalMinor: Minor })
export type BookingRentalPayouts = z.infer<typeof BookingRentalPayouts>

export const BOOKING_RENTAL_DEAL_TYPES = ['flat', 'revenue_share', 'flat_plus_share'] as const
export type BookingRentalDealType = (typeof BOOKING_RENTAL_DEAL_TYPES)[number]

export const BookingRentalSettlementTerms = z.object({
  version: z.literal(1),
  asOf: Iso,
  rental: z.object({ sourceId: Id, version: Version, title: Text(300), status: Text(40), rentalType: z.enum(['standard', 'ticketed']), renterName: Text(200).nullable() }),
  deal: z.object({
    dealType: z.enum(BOOKING_RENTAL_DEAL_TYPES).nullable(),
    flatFeeMinor: Minor.nullable(),
    revShareBps: z.number().int().nullable(),
    revShareThresholdMinor: Minor.nullable(),
    ticketingFeeBps: z.number().int(),
    ascapBps: z.number().int(),
    cardFeeBps: z.number().int(),
  }),
  tiers: z.array(z.object({ sourceId: Id, name: Text(200), capacity: z.number().int(), priceMinor: Minor, facilityFeeMinor: Minor, comps: z.number().int() })),
  dates: z.array(z.object({ date: Day.nullable(), type: Text(40), startTime: Text(10).nullable(), endTime: Text(10).nullable() })),
  bookingSettlement: z
    .object({
      ticketsSold: z.number().int(),
      grossMinor: Minor,
      facilityFeesMinor: Minor,
      ticketingFeesMinor: Minor,
      netMinor: Minor,
      expenses: z.array(z.object({ label: Text(200), amountMinor: Minor })),
      totalExpensesMinor: Minor,
      renterPayoutMinor: Minor,
      breNetMinor: Minor,
      finalizedAt: Iso.nullable(),
      updatedAt: Iso.nullable(),
      tierActuals: z.array(z.object({ tierId: Id, ticketsSold: z.number().int(), compsUsed: z.number().int() })),
    })
    .nullable(),
  payouts: z.array(BookingRentalPayout),
  payoutsTotalMinor: Minor,
  settlementInHub: z.boolean(),
})
export type BookingRentalSettlementTerms = z.infer<typeof BookingRentalSettlementTerms>

export type RentalDealProposal = {
  // Null when Booking's terms can't become a Hub deal (no deal type set).
  terms: SettlementDealTerms | null
  // Expense lines Booking's own settlement carried, to add as lines charged to the renter.
  expenses: { label: string; amountCents: number }[]
  notes: string[]
}

/**
 * Booking's ticketed-rental deal as Hub's rental deal (ADR 0046): the "copy
 * from Booking" path the talent deal already has (ADR 0039). Booking's deal
 * types map to Hub's formulas on the waterfall's net:
 *   flat             → flat (rent is the flat fee)
 *   revenue_share    → plus with a zero flat amount: the share of the net above the threshold
 *   flat_plus_share  → plus: the flat fee plus the share above the threshold
 * The facility fees: one fee on every tier rides as the per-ticket fee
 * (times BTownTix's paid tickets); fees that differ by tier are a fixed
 * amount staff confirm (Booking's own saved figure, when it has one). Early
 * payouts become advances already paid to the renter.
 */
export function rentalDealFromBooking(read: BookingRentalSettlementTerms, opts: { organizationId: string | null; copiedAt: string }): RentalDealProposal {
  const d = read.deal
  const notes: string[] = []
  const expenses = (read.bookingSettlement?.expenses ?? []).filter((e) => e.amountMinor > 0).map((e) => ({ label: e.label, amountCents: e.amountMinor }))
  if (!d.dealType) return { terms: null, expenses, notes: ['This rental has no deal type. Set the deal in Hub.'] }
  if (read.rental.rentalType !== 'ticketed') notes.push('This rental isn’t ticketed; check the door really belongs to the renter.')
  const fees = [...new Set(read.tiers.map((t) => t.facilityFeeMinor))]
  let perTicketFeeCents: number | null = null
  let facilityFeesCents: number | null = 0
  if (fees.length === 1 && fees[0]! > 0) {
    perTicketFeeCents = fees[0]!
    notes.push(`Every tier carries a ${(fees[0]! / 100).toFixed(2)} facility fee, used as the per-ticket fee.`)
  } else if (fees.length > 1) {
    facilityFeesCents = read.bookingSettlement ? read.bookingSettlement.facilityFeesMinor : null
    notes.push(
      read.bookingSettlement
        ? `Facility fees differ by tier; the rental’s saved settlement counted ${(read.bookingSettlement.facilityFeesMinor / 100).toFixed(2)}. Check it against BTownTix’s sales by tier.`
        : 'Facility fees differ by tier: enter the total inside the door (each tier’s fee × its tickets sold) before the statement can be worked out.',
    )
  }
  if (d.ticketingFeeBps) notes.push(`The rental assumed ${(d.ticketingFeeBps / 100).toFixed(2)}% ticketing fees. None are taken from the door unless you enter an amount.`)
  const ticketingFeesCents = read.bookingSettlement?.ticketingFeesMinor ?? 0
  const formula: SettlementDealTerms['formula'] = d.dealType === 'flat' ? 'flat' : 'plus'
  const terms: SettlementDealTerms = {
    counterpartyName: read.rental.renterName ?? 'Renter',
    counterpartyRole: 'renter',
    organizationId: opts.organizationId,
    kind: 'rental',
    formula,
    flatCents: d.dealType === 'revenue_share' ? 0 : (d.flatFeeMinor ?? 0),
    percentBps: d.dealType === 'flat' ? null : (d.revShareBps ?? 0),
    basis: 'net_after_expenses',
    splitPointCents: d.dealType === 'flat' ? null : (d.revShareThresholdMinor ?? 0),
    capCents: null,
    doorCollectedBy: 'bre',
    perTicketFeeCents,
    barSplitBps: null,
    merchSoldBy: 'none',
    merchCommissionBps: null,
    advances: read.payouts.map((p) => ({ direction: 'to_counterparty' as const, label: `Early payout (${p.method})`, amountCents: p.amountMinor, paidOn: p.paidOn, reference: `Early payout ${p.sourceId}`.slice(0, 200) })).filter((a) => a.amountCents > 0),
    adjustments: [],
    origin: { kind: 'booking', sourceId: read.rental.sourceId, entityType: 'rental', source: 'contract', status: read.rental.status, dealType: d.dealType, copiedAt: opts.copiedAt },
    note: null,
    waterfall: { facilityFeesCents, ticketingFeesCents, ascapBps: d.ascapBps, cardFeeBps: d.cardFeeBps },
  }
  if (expenses.length) notes.push(`The rental’s settlement listed ${expenses.length} expense${expenses.length === 1 ? '' : 's'}; add them as expenses charged to the renter so they come off before BRE’s share.`)
  return { terms, expenses, notes }
}

/**
 * What Booking's own rental settlement screen would say for these figures:
 * its arithmetic exactly (REAL dollars, rounded only when shown), so Hub's
 * statement can be compared with it and any difference explained (Phase 6's
 * gate). Inputs are cents; percentages are Booking's percent values.
 */
export function bookingRentalWaterfall(input: {
  grossCents: number
  facilityFeesCents: number
  ticketingFeesCents: number
  ascapPct: number
  ccFeePct: number
  expensesCents: number[]
  dealType: BookingRentalDealType | null
  flatFeeCents: number | null
  revSharePct: number | null
  revShareThresholdCents: number | null
  payoutsCents: number[]
}) {
  const $ = (c: number) => c / 100
  const adjGross = $(input.grossCents) - $(input.facilityFeesCents)
  const netAfterTicketing = adjGross - $(input.ticketingFeesCents)
  const ascap = netAfterTicketing * (input.ascapPct / 100)
  const cc = netAfterTicketing * (input.ccFeePct / 100)
  const netAfterVariable = netAfterTicketing - ascap - cc
  const totalExpenses = input.expensesCents.reduce((a, c) => a + $(c), 0)
  const net = netAfterVariable - totalExpenses
  let breNet: number
  const threshold = input.revShareThresholdCents === null ? 0 : $(input.revShareThresholdCents)
  if (input.dealType === 'flat') breNet = input.flatFeeCents === null ? 0 : $(input.flatFeeCents)
  else if (input.dealType === 'revenue_share') breNet = Math.max(0, net - threshold) * ((input.revSharePct ?? 0) / 100)
  else if (input.dealType === 'flat_plus_share') breNet = (input.flatFeeCents === null ? 0 : $(input.flatFeeCents)) + Math.max(0, net - threshold) * ((input.revSharePct ?? 0) / 100)
  else breNet = net
  const renterPayout = net - breNet
  const payouts = input.payoutsCents.reduce((a, c) => a + $(c), 0)
  const cents = (d: number) => Math.round(d * 100)
  return { ascapCents: cents(ascap), cardCents: cents(cc), netCents: cents(net), breNetCents: cents(breNet), renterPayoutCents: cents(renterPayout), netProceedsCents: cents(renterPayout - payouts) }
}

// --------------------------------------------------------------- closeout

export const CLOSEOUT_ITEM_KEYS = ['event_over', 'deposit', 'final_invoice', 'incidentals', 'insurance', 'documents', 'inspection'] as const
export type CloseoutItemKey = (typeof CLOSEOUT_ITEM_KEYS)[number]
export const WAIVABLE_CLOSEOUT_ITEMS = ['insurance', 'documents', 'inspection'] as const
export const DEPOSIT_DISPOSITIONS = ['applied', 'returned', 'none'] as const
export type DepositDisposition = (typeof DEPOSIT_DISPOSITIONS)[number]

export const CloseoutItem = z.object({
  key: z.string().max(40),
  label: Text(200),
  state: z.enum(['done', 'open', 'waived', 'not_applicable']),
  detail: Text(2000),
  waivable: z.boolean(),
  waiverReason: Text(500).nullable().optional(),
})
export type CloseoutItem = z.infer<typeof CloseoutItem>

export const BookingRentalCloseout = z.object({
  version: z.literal(1),
  asOf: Iso,
  rental: z.object({ sourceId: Id, version: Version, title: Text(300), status: Text(40), rentalType: z.enum(['standard', 'ticketed']) }),
  closeout: z
    .object({
      status: z.enum(['pending', 'in_review', 'closed']),
      inspectedAt: Iso.nullable(),
      inspectedByName: Text(200).nullable(),
      inspectionNotes: Text(4000).nullable(),
      damageNotes: Text(4000).nullable(),
      followUpNotes: Text(4000).nullable(),
      recommendedChargeMinor: Minor.nullable(),
      chargedMinor: Minor.nullable(),
      closedAt: Iso.nullable(),
      closedByName: Text(200).nullable(),
      closedChannel: Text(20).nullable(),
      depositDisposition: z.enum(DEPOSIT_DISPOSITIONS).nullable(),
      checklistAtClose: z.array(CloseoutItem).nullable(),
    })
    .nullable(),
  checklist: z.object({ items: z.array(CloseoutItem), ready: z.boolean(), closed: z.boolean() }),
})
export type BookingRentalCloseout = z.infer<typeof BookingRentalCloseout>

// Hub's own closeout items, beside Booking's: the damage and setup work
// orders Hub opened for the rental, and (for a ticketed rental linked to a
// Hub event) the settlement. Booking doesn't know about either, so Hub
// checks them before it sends the closing command; Booking re-checks its own.
export type HubCloseoutItem = { key: 'work_orders' | 'settlement'; label: string; state: 'done' | 'open' | 'not_applicable'; detail: string }

export function hubCloseoutItems(input: {
  workOrders: { number: number; status: string; type: string }[]
  // Open work orders of the rental the person can't see (counted, never shown).
  hiddenOpen?: number
  rentalType: 'standard' | 'ticketed'
  linkedEventId: string | null
  settlementPhase: string | null
}): HubCloseoutItem[] {
  const open = input.workOrders.filter((w) => w.status !== 'done' && w.status !== 'canceled')
  const hidden = input.hiddenOpen ?? 0
  const words = [open.length ? open.map((w) => `WO-${w.number}`).join(', ') : null, hidden ? `${hidden} you can’t see` : null].filter(Boolean).join(' and ')
  const items: HubCloseoutItem[] = [
    {
      key: 'work_orders',
      label: 'Work orders done',
      state: input.workOrders.length === 0 && hidden === 0 ? 'not_applicable' : open.length === 0 && hidden === 0 ? 'done' : 'open',
      detail: input.workOrders.length === 0 && hidden === 0 ? 'No work orders were opened for this rental.' : open.length === 0 && hidden === 0 ? `All ${input.workOrders.length} done or canceled.` : `Still open: ${words}.`,
    },
  ]
  if (input.rentalType === 'ticketed') {
    const phase = input.settlementPhase
    items.push({
      key: 'settlement',
      label: 'Settlement closed in Hub',
      state: phase === 'closed' ? 'done' : 'open',
      detail: !input.linkedEventId ? 'Link the rental to its Hub event and settle it there (ADR 0039).' : phase === null ? 'No settlement has been started for the linked event.' : phase === 'closed' ? 'Closed.' : `The settlement is ${phase}.`,
    })
  }
  return items
}

// --------------------------------------------------- rental ↔ Hub event

// Which Hub event a Booking rental belongs to (ADR 0046). Hub suggests by
// date and room; a person confirms. Nothing is ever linked automatically,
// and a suggestion is marked "clear" only when exactly one event shares both
// a day and a room with the rental: any tie, or a match on only one of the
// two, is ambiguous and says so.
export type RentalForLinking = { days: string[]; bookingSpaceIds: string[]; facilityIds: string[] }
export type EventForLinking = { id: string; name: string; startsOn: string; endsOn: string; status: string; bookingSpaceIds: string[]; facilityIds: string[] }
export type RentalEventSuggestion = { eventId: string; name: string; startsOn: string; endsOn: string; sharesDay: boolean; sharesRoom: boolean; sharesFacility: boolean; score: number; reasons: string[] }

export function suggestRentalEventLinks(rental: RentalForLinking, events: EventForLinking[]): { suggestions: RentalEventSuggestion[]; clear: string | null; ambiguous: boolean } {
  const days = new Set(rental.days)
  const rooms = new Set(rental.bookingSpaceIds)
  const out: RentalEventSuggestion[] = []
  for (const e of events) {
    if (e.status === 'canceled' || e.status === 'cancelled') continue
    const sharesDay = rental.days.some((d) => d >= e.startsOn && d <= e.endsOn)
    if (!sharesDay) continue
    const sharesRoom = e.bookingSpaceIds.some((s) => rooms.has(s))
    const sharesFacility = !sharesRoom && e.facilityIds.some((f) => rental.facilityIds.includes(f))
    const common = [...days].filter((d) => d >= e.startsOn && d <= e.endsOn).length
    const reasons = [
      `${common} day${common === 1 ? '' : 's'} in common`,
      sharesRoom ? 'the same rental space' : sharesFacility ? 'the same facility (a different space)' : 'no space or facility in common',
    ]
    out.push({ eventId: e.id, name: e.name, startsOn: e.startsOn, endsOn: e.endsOn, sharesDay, sharesRoom, sharesFacility, score: (sharesRoom ? 2 : sharesFacility ? 1 : 0) + 1, reasons })
  }
  out.sort((a, b) => b.score - a.score || a.startsOn.localeCompare(b.startsOn) || a.name.localeCompare(b.name))
  const strong = out.filter((s) => s.sharesDay && s.sharesRoom)
  const clear = strong.length === 1 ? strong[0]!.eventId : null
  return { suggestions: out, clear, ambiguous: clear === null && out.length > 0 }
}

/** The venue-local days a rental occupies, from Booking's room times (setup and cleanup days included). */
export function rentalDays(spaces: { startLocal: string | null; endLocal: string | null }[], ticketedDates: { date: string | null }[] = []): string[] {
  const days = new Set<string>()
  for (const s of spaces) {
    if (!s.startLocal) continue
    let d = s.startLocal.slice(0, 10)
    const last = (s.endLocal ?? s.startLocal).slice(0, 10)
    // An end at or before 06:00 belongs to the night before (a wedding until 1 a.m.).
    const end = s.endLocal && s.endLocal.slice(11) <= '06:00' && last > d ? new Date(Date.parse(`${last}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10) : last
    for (let i = 0; d <= end && i < 62; i += 1) {
      days.add(d)
      d = new Date(Date.parse(`${d}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10)
    }
  }
  for (const t of ticketedDates) if (t.date) days.add(t.date)
  return [...days].sort()
}

export const RentalEventLink = z.object({
  id: z.string(),
  rentalId: Id,
  eventId: z.string(),
  eventName: z.string(),
  eventStartsOn: Day,
  eventEndsOn: Day,
  basis: z.enum(['suggested', 'manual']),
  linkedByName: z.string().nullable(),
  linkedAt: z.string(),
})
export type RentalEventLink = z.infer<typeof RentalEventLink>
